import {useNavigate, useSearchParams} from 'react-router-dom';
import Grid from '@mui/material/Grid';
import * as React from 'react';
import {useEffect, useState} from 'react';
import useRootHook from '../../hooks/useRoot';
import helpers from '../../helpers/common';
import PayMethodForm from './PayMethodForm';
import paysystems from '../../models/paysystems';
import paymethods from '../../models/paymethods';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';

export default function PayMethodEdit() {
    const [searchParams] = useSearchParams();
    const [payMethodData, setPayMethodData] = useState(null);
    const [paySystemsData, setPaySystemsData] = useState([]);
    const [mapper, setMapper] = useState({
        keyToFieldMapper: {},
        fieldToKeyMapper: {}
    });

    const [fields, setFields] = useState({});
    const [errors, setErrors] = useState({});

    const {setShowLoader} = useRootHook();
    const navigate = useNavigate();

    useEffect(() => {
        if (payMethodData) return;
        setShowLoader(true);

        const promises = [
            paysystems.getData(),
            paymethods.get(searchParams.get('id')),
            //paymethods.getBalance(searchParams.get('id')),
            //paymethods.getBalanceTransactions(searchParams.get('id'))
        ];

        Promise.all(promises)
            .then(([psysdata, pmdata, providerBalance, providerBalanceTransactions]) => {
                setShowLoader(false);
                psysdata && setPaySystemsData(psysdata);
                pmdata && setPayMethodData(pmdata);
            }).catch(() => setShowLoader(false));

    }, []);

    useEffect(() => {
        if (!paySystemsData || paySystemsData.length === 0 || !payMethodData) return;
        const newMapper = setMapperByRecordID(payMethodData.paysystem_id);
        const fieldsData = paymethods.filterFormFields(payMethodData, Object.keys(newMapper.fieldToKeyMapper));
        setFields((prevFields) => {
            return {...prevFields, ...fieldsData, paysystem_id: payMethodData.paysystem_id, id: payMethodData.id};
        });
    }, [payMethodData, paySystemsData]);

    const setMapperByRecordID = (id) => {
        const record = paySystemsData.find(system => system.id === id);
        if (!record || !record.paymethod_field_mapper) return;
        let recordMapper = {...record.paymethod_field_mapper};
        const systemKey = String(record.key || '').toLowerCase();

        // Backward-compatible mapper defaults for old paysystem records.
        if (systemKey.includes('avrepay')) {
            recordMapper = {
                ...recordMapper,
                onsiteS2S: recordMapper.onsiteS2S || 'var8',
                testMode: recordMapper.testMode || 'var9',
                s2sToken: recordMapper.s2sToken || 'var10',
                skipCapture: recordMapper.skipCapture || 'var11',
            };
        }
        if (systemKey.includes('affor')) {
            recordMapper = {
                ...recordMapper,
                onsiteS2S: recordMapper.onsiteS2S || 'var10',
            };
        }
        if (systemKey === 'stbl') {
            recordMapper = {
                domain: recordMapper.domain || 'var1',
                apiToken: recordMapper.apiToken || 'var2',
                secretKey: recordMapper.secretKey || 'var3',
                transferMethod: recordMapper.transferMethod || 'var4',
                bankName: recordMapper.bankName || 'var5',
                successUrl: recordMapper.successUrl || 'var6',
                failedUrl: recordMapper.failedUrl || 'var7',
                authScheme: recordMapper.authScheme || 'var8',
                callbackVerifyMode: recordMapper.callbackVerifyMode || 'var9',
                apiBase: recordMapper.apiBase === 'var4' ? 'var10' : (recordMapper.apiBase || 'var10'),
            };
        }

        const newMapper = {keyToFieldMapper: recordMapper, fieldToKeyMapper: helpers.invertObject(recordMapper)};
        setMapper(newMapper);
        setFields((prevFields) => {
            return {...payMethodData, ...prevFields};
        });
        return newMapper;
    };

    const handleChange = (ev) => {
        const [, value, name] = helpers.getFormEventData(ev);

        if (name === 'paysystem_id') setMapperByRecordID(parseInt(value));

        setErrors({...errors, [name]: undefined});

        setFields((prevState) => {
            let newFields = {...prevState};
            newFields[name] = value;

            return newFields;
        });
    };

    const handleBlur = (ev) => {
        const [, , name] = helpers.getFormEventData(ev);

        if (!paymethods.FORM_FIELDS.concat(Object.keys(mapper.fieldToKeyMapper)).includes(name)) return;

        const newErrors = {...errors};
        const newFields = {...fields};

        typeof paymethods.fieldValidator[name] === 'function'
        && paymethods.fieldValidator[name](newFields, newErrors);
        setErrors(newErrors);
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        let filteredFields = paymethods.filterFormFields(fields, Object.keys(mapper.fieldToKeyMapper));
        const validationErrors = paymethods.methodValidator.edit(filteredFields);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) return;

        setShowLoader(true);
        paymethods.edit(filteredFields)
            .then(() => {
                setShowLoader(false);
                navigate('/paymethods');
            }).catch(() => setShowLoader(false));
    };

    if (!payMethodData || !paySystemsData || paySystemsData.length === 0) return null;

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title={`Edit paymethod: ${payMethodData.name}`}
                breadcrumbs={[['Dashboard', '/'], ['Methods', '/paymethods'], ['Edit', `/paymethods/edit?id=${payMethodData.id}`]]}
            />
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
                <PayMethodForm paySystemsData={paySystemsData} mapper={mapper} fields={fields} errors={errors}
                               handleChange={handleChange}
                               handleBlur={handleBlur} handleSubmit={handleSubmit} project_id={fields.project_id}/>
            </SectionCard>
        </Grid>
    </React.Fragment>;
}